import PropTypes from 'prop-types';
import { Box, Typography, Button, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RefreshOutlined } from '@mui/icons-material';

const AccountsHeader = ({ dateRange, onDateChange, onRefresh }) => {
    return (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 60%, #42a5f5 100%)',
                    px: { xs: 2, md: 4 },
                    pt: 4,
                    pb: 7,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '12px',
                    mb: 4,
                    boxShadow: '0 10px 30px rgba(25, 118, 210, 0.15)'
                }}
            >
                {/* Decorative circles - Matching HR Dashboard */}
                {[
                    { size: 240, top: -90, right: -70, op: 0.08 },
                    { size: 140, top: 20, right: 140, op: 0.06 }
                ].map((c, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            top: c.top,
                            right: c.right,
                            width: c.size,
                            height: c.size,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            opacity: c.op
                        }}
                    />
                ))}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 3,
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <Box>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80', boxShadow: '0 0 10px #4ade80' }} />
                            <Typography
                                variant="overline"
                                sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: '0.15em', fontWeight: 700, fontSize: '0.75rem' }}
                            >
                                ACCOUNTS CONSOLE
                            </Typography>
                        </Stack>
                        <Typography variant="h3" fontWeight={800} sx={{ color: '#fff', mt: 0.5, letterSpacing: '-0.02em' }}>
                            Financial Overview
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, maxWidth: 500, lineHeight: 1.6 }}>
                            Real-time monitoring of account heads, payment disbursements, and allowance tracking across departments.
                        </Typography>
                    </Box>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    bgcolor: 'rgba(255,255,255,0.12)',
                                    p: 0.75,
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(12px)'
                                }}
                            >
                                <Box sx={{ px: 1.5, py: 0.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'block', mb: -0.5 }}
                                    >
                                        FROM
                                    </Typography>
                                    <DatePicker
                                        value={dateRange.from}
                                        onChange={(val) => onDateChange('from', val)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                variant: 'standard',
                                                sx: {
                                                    width: 110,
                                                    '& .MuiInputBase-root': { color: '#fff', fontSize: '0.9rem', fontWeight: 700 },
                                                    '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' }
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ width: '1px', bgcolor: 'rgba(255,255,255,0.2)', my: 1 }} />
                                <Box sx={{ px: 1.5, py: 0.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'block', mb: -0.5 }}
                                    >
                                        TO
                                    </Typography>
                                    <DatePicker
                                        value={dateRange.to}
                                        onChange={(val) => onDateChange('to', val)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                variant: 'standard',
                                                sx: {
                                                    width: 110,
                                                    '& .MuiInputBase-root': { color: '#fff', fontSize: '0.9rem', fontWeight: 700 },
                                                    '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' }
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                onClick={onRefresh}
                                startIcon={<RefreshOutlined />}
                                sx={{
                                    bgcolor: '#fff',
                                    color: '#1e3a8a',
                                    fontWeight: 800,
                                    borderRadius: '10px',
                                    px: 3.5,
                                    py: 1.4,
                                    textTransform: 'none',
                                    fontSize: '0.9rem',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                    '&:hover': {
                                        bgcolor: '#f8fafc',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 25px rgba(0,0,0,0.25)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                Sync Data
                            </Button>
                        </Stack>
                    </LocalizationProvider>
                </Box>
            </Box>
        </motion.div>
    );
};

AccountsHeader.propTypes = {
    dateRange: PropTypes.shape({
        from: PropTypes.any,
        to: PropTypes.any
    }).isRequired,
    onDateChange: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired
};

export default AccountsHeader;
